import { useEffect, useMemo, useRef, useState } from 'react';
import GSApi from 'api/Api';
import PlaybackManager from 'providers/player/PlaybackManager';
import { usePlayer } from 'providers/player/PlayerContext';
import { useEpgStore } from 'providers/useEpgStore/EpgContext';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { FetchParamById } from 'types/ApiTypes';
import { PlayerEvents, PlayingMode } from 'types/Player';
import { buildEpisodeTitle, getListingCapability } from 'utils/fnData';
import { isLivePlayerAsset } from 'utils/fnTypeGuards';
import { SECONDS } from 'utils/TimeUnit';

/**
 * Keeps the playing program of a live channel in sync with what is actually on screen.
 *
 * The program is resolved on a tick rather than from a timer armed for the program end: a single timer has to
 * fire exactly on the boundary to resolve the right program, and anything that makes it miss (clock drift,
 * throttling, a lookup that finds nothing) leaves the player stuck on the program that ended.
 *
 * Returns the program plus the asset fields derived from it, so the player page can assemble its asset, and
 * restates those fields on the playing asset itself whenever the two drift apart — switching playback mode
 * rebuilds the asset from PlaybackManager's own copy, which carries whichever program was current when the
 * stream session was created.
 */

const PROGRAM_CHECK_INTERVAL_MS = 5_000;
const PROGRAM_REFETCH_COOLDOWN_MS = 60_000;

const toMilliseconds = (time: Date | number): number => (time instanceof Date ? time.getTime() : time);

export const useLiveProgramSync = (channelId: string) => {
    const { isGuest } = useAuth();
    const { programs, getProgramsByChannels } = useEpgStore();
    const { channels } = useChannels();
    const { asset: currentPlayerAsset, setAsset, playingMode } = usePlayer();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();

    const [currentProgram, setCurrentProgram] = useState(undefined);
    const [programsFetchCount, setProgramsFetchCount] = useState(0);

    const programIdRef = useRef<string>(null);
    const programWatcherRef = useRef<() => void>(null);
    const lastProgramFetchRef = useRef(0);
    const castFetchedForListingId = useRef<string | null>(null);

    const currentChannel = useMemo(() => channels?.find((channel) => channel.id === channelId), [channels, channelId]);

    const { response: listingDetails, fetcher: listingDetailsFetcher } = useDataFetcher<any, FetchParamById>(
        (param) => GSApi.fetchListing(param.id),
        onNoInternet,
        onNetworkError
    );

    const fetchProgramsIfNeeded = async () => {
        if (!channelId) return;
        await getProgramsByChannels([channelId], Date.now() / 1000);
        setProgramsFetchCount((c) => c + 1);
    };

    // Program metadata is only available when the channel publishes EPG data, duh
    const buildProgramTiming = (program) => {
        const startTime = program ? toMilliseconds(program.startTime) : undefined;
        const endTime = program ? toMilliseconds(program.endTime) : undefined;

        return {
            title: program?.title,
            startTime,
            endTime,
            duration: program ? (endTime - startTime) / 1000 : 0,
        };
    };

    const buildProgramDetails = (program) => {
        const { replayUntil, recordingUntil } = getListingCapability(program ?? {}, currentChannel);
        const isDetailsForProgram = listingDetails?.id === program?.id;

        return {
            listingId: program?.id,
            recordingUntil,
            replayUntil,
            episode_title: program?.episodeTitle,
            image: program?.image,
            subtitle: buildEpisodeTitle(program?.episodeTitle, program?.seasonNumber, program?.episodeNumber),
            description: program?.description,
            cast: isDetailsForProgram ? listingDetails?.program?.credits : undefined,
            genres: program?.genres?.map((g: string) => ({ Value: g })),
            releaseYear: program?.releaseYear,
            countries: program?.countries,
            seriesId: program?.seriesId ?? (isDetailsForProgram ? listingDetails?.program?.program_series_id : undefined),
        };
    };

    const findProgramAt = (time: number) => {
        if (!channelId) return null;

        const channelPrograms: any[] = programs[channelId] ? Object.values(programs[channelId]).flat() : [];

        if (!channelPrograms?.length) return null;

        return (
            channelPrograms.find((obj) => {
                if (obj.channelId !== channelId) return false;

                return toMilliseconds(obj.startTime) <= time && time < toMilliseconds(obj.endTime);
            }) ?? null
        );
    };

    // The live edge follows the clock, while time shifted playback follows the playhead
    const getPlayheadTime = () => {
        if (playingMode !== PlayingMode.TIME_SHIFT || !isLivePlayerAsset(currentPlayerAsset) || !currentPlayerAsset.startTime) {
            return Date.now();
        }

        return currentPlayerAsset.startTime + SECONDS.toMillis(PlaybackManager.getProgress());
    };

    const isProgramInfoStale = (program) => {
        if (!program || !isLivePlayerAsset(currentPlayerAsset)) return false;
        if (program.channelId !== channelId) return false;

        // Right after a channel switch the asset still belongs to the channel that was playing before
        if (currentPlayerAsset.channel_id && currentPlayerAsset.channel_id !== channelId) return false;

        return currentPlayerAsset.listingId !== program.id || currentPlayerAsset.startTime !== toMilliseconds(program.startTime);
    };

    // Leaves the stream session fields of the playing asset untouched
    const resyncProgramInfo = (program) => {
        setAsset({
            ...currentPlayerAsset,
            ...buildProgramTiming(program),
            ...buildProgramDetails(program),
        });
    };

    const syncProgram = () => {
        if (!channelId) return;

        const program = findProgramAt(getPlayheadTime());

        if (!program) {
            // Nothing to show from the cached EPG day
            if (Date.now() - lastProgramFetchRef.current < PROGRAM_REFETCH_COOLDOWN_MS) return;

            lastProgramFetchRef.current = Date.now();
            fetchProgramsIfNeeded();
            return;
        }

        if (program.id !== programIdRef.current) {
            programIdRef.current = program.id;
            setCurrentProgram(program);
        }

        if (isProgramInfoStale(program)) {
            resyncProgramInfo(program);
        }
    };

    programWatcherRef.current = syncProgram;

    useEffect(() => {
        if (!channelId) return undefined;

        const intervalId = window.setInterval(() => programWatcherRef.current?.(), PROGRAM_CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [channelId]);

    useEffect(() => {
        fetchProgramsIfNeeded();
    }, [channelId]);

    useEffect(() => {
        syncProgram();
    }, [channelId, programs, programsFetchCount]);

    useEffect(() => {
        const onInfoRequested = () => {
            if (!currentProgram?.id || isGuest || castFetchedForListingId.current === currentProgram.id) {
                return;
            }

            castFetchedForListingId.current = currentProgram.id;
            listingDetailsFetcher({ id: currentProgram.id });
        };

        document.addEventListener(PlayerEvents.INFO_REQUESTED, onInfoRequested);

        return () => {
            document.removeEventListener(PlayerEvents.INFO_REQUESTED, onInfoRequested);
        };
    }, [currentProgram?.id, isGuest]);

    const programTiming = useMemo(() => buildProgramTiming(currentProgram), [currentProgram]);

    const programDetails = useMemo(() => buildProgramDetails(currentProgram), [currentProgram, listingDetails, currentChannel]);

    return {
        currentProgram,
        programTiming,
        programDetails,
        syncProgram,
    };
};
